import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import dynamic from 'next/dynamic';
import {
  AppBar, Box, Button, Chip, Container, CssBaseline, Divider, Drawer, Grid, Menu, MenuItem,
  Link, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, Typography, Tabs, Tab
} from "@mui/material";
import { styled, alpha } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';

// Icons
import ContentPasteTwoToneIcon from '@mui/icons-material/ContentPasteTwoTone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';

// TreeView
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import { animated, useSpring } from '@react-spring/web';

// Data Imports
import getPageMetaTags from "../../../lib/meta/_getMetaTags";
import { dataConstants } from "../../../src/constants";
import {
  healthCheck, healthCheckResponse, historicalMetrics,
  historicalMetricsResponse, stockMetrics, stockMetricsResponse
} from "../../../src/components/APIDocs";

// Dynamically load SyntaxHighlighter
const SyntaxHighlighter = dynamic(() => import('react-syntax-highlighter').then(mod => mod.Prism), { ssr: false });

/* ==============================================================================
   1. CONSTANTS, THEME, & SHARED STYLES
   ============================================================================== */

const THEME_COLORS = {
  bgDark: '#0a192a',
  bgPanel: '#293f5a',
  bgCode: '#001E3C',
  border: '#132F4C',
  textMuted: 'rgb(178, 186, 194)',
  textDark: '#4f566b',
  textHeading: '#2a2f45',
  primary: '#174c86',
  primaryHover: 'rgb(80, 144, 211)',
  chipBg: 'rgb(242, 242, 242)',
  methodGet: 'rgb(12, 187, 82)'
};

const SECTION_HEADING_SX = {
  '& a': { color: THEME_COLORS.textDark },
  '&:hover a': { color: THEME_COLORS.primaryHover },
  scrollMarginTop: "45px",
  zIndex: 9, pt: '3rem', mb: 3, lineHeight: '1.5',
  color: THEME_COLORS.textHeading, fontSize: "2.4rem", fontWeight: 600
};

/* ==============================================================================
   2. STYLED COMPONENTS
   ============================================================================== */

const StyledTreeItemLabel = styled(Typography)({ color: 'inherit', fontWeight: 'inherit', flexGrow: 1 });

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? theme.palette.grey[800] : theme.palette.grey[400],
  position: 'relative',
  [`& .${treeItemClasses.content}`]: {
    borderRadius: 0, padding: theme.spacing(0.9, 0.9, 0.9, 0), fontWeight: 500,
    [`& .${treeItemClasses.label}`]: { fontWeight: 'inherit' },
    [`& .${treeItemClasses.iconContainer}`]: { marginLeft: theme.spacing(.6) },
    [`&.Mui-expanded `]: {
      '&:not(.Mui-focused, .Mui-selected, .Mui-selected.Mui-focused) .labelIcon': {
        color: theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.secondary.dark,
      },
      '&::before': {
        content: '""', display: 'block', position: 'absolute', left: '16px', top: '44px', height: 'calc(100% - 52px)', width: '1.5px',
        backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      },
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.mode === 'light' ? theme.palette.primary.main : 'white',
    },
    [`&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused`]: {
      borderRight: `2px solid ${theme.palette.primary.main}`, color: theme.palette.primary.main,
    },
  },
  [`& .${treeItemClasses.groupTransition}`]: { [`& .${treeItemClasses.content}`]: { fontWeight: 500 } },
}));

const AnimatedCollapse = animated(Collapse);
const TransitionComponent = React.memo(function TransitionComponent(props) {
  const style = useSpring({ to: { opacity: props.in ? 1 : 0, transform: `translate3d(0,${props.in ? 0 : 20}px,0)` } });
  return <AnimatedCollapse style={style} {...props} />;
});

const StyledTreeItem = React.forwardRef(function StyledTreeItem(props, ref) {
  const { labelIcon: LabelIcon, labelText, href, sx, ...other } = props;
  return (
    <StyledTreeItemRoot
      slots={{ groupTransition: TransitionComponent }}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit !important', ...sx }}>
          {LabelIcon && <Box component={LabelIcon} className="labelIcon" color="inherit" sx={{ mr: 0, fontSize: '1.2rem' }} />}
          <StyledTreeItemLabel variant="body2">{labelText}</StyledTreeItemLabel>
        </Box>
      }
      {...other} ref={ref}
    />
  );
});

const StyledMenu = styled((props) => <Menu elevation={0} {...props} />)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 7, marginTop: theme.spacing(1), minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -2px rgba(0,0,0,0.05)',
    '& .MuiMenuItem-root': { minHeight: '32px' },
  },
}));

const LightTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: { color: THEME_COLORS.bgPanel },
  [`& .${tooltipClasses.tooltip}`]: { backgroundColor: THEME_COLORS.bgPanel, fontWeight: 600, color: '#fff', boxShadow: theme.shadows[3], fontSize: 12, padding: '7px 15px' },
}));

/* ==============================================================================
   3. DATA CONFIGURATIONS
   ============================================================================== */

const languages = [
  { lang: 'csharp', value: 'C# - HttpClient', label: 'C# - HttpClient' },
  { lang: 'csharp', value: 'C# - RestSharp', label: 'C# - RestSharp' },
  { lang: 'curl', value: 'cURL', label: 'cURL ' },
  { lang: 'dart', value: 'Dart - http', label: 'Dart - http' },
  { lang: 'go', value: 'Go - Native', label: 'Go - Native' },
  { lang: 'http', value: 'HTTP - HTTP', label: 'HTTP - HTTP' },
  { lang: 'java', value: 'Java - OkHttp', label: 'Java - OkHttp' },
  { lang: 'java', value: 'Java - Unirest', label: 'Java - Unirest' },
  { lang: 'javascript', value: 'JavaScript - Fetch', label: 'JavaScript - Fetch' },
  { lang: 'javascript', value: 'JavaScript - jQuery', label: 'JavaScript - jQuery' },
  { lang: 'javascript', value: 'JavaScript - XHR', label: 'JavaScript - XHR' },
  { lang: 'c', value: 'C - libcurl', label: 'C - libcurl' },
  { lang: 'nodejs', value: 'NodeJs - Axios', label: 'NodeJs - Axios' },
  { lang: 'nodejs', value: 'NodeJs - Native', label: 'NodeJs - Native' },
  { lang: 'nodejs', value: 'NodeJs - Request', label: 'NodeJs - Request' },
  { lang: 'nodejs', value: 'NodeJs - Unirest', label: 'NodeJs - Unirest' },
  { lang: 'objectivec', value: 'Objective-C - NSURLSession', label: 'Objective-C - NSURLSession' },
  { lang: 'ocaml', value: 'OCaml - Cohttp', label: 'OCaml - Cohttp' },
  { lang: 'php', value: 'PHP - cURL', label: 'PHP - cURL' },
  { lang: 'php', value: 'PHP - Guzzle', label: 'PHP - Guzzle' },
  { lang: 'php', value: 'PHP - HTTP_Request2', label: 'PHP - HTTP_Request2' },
  { lang: 'php', value: 'PHP - pecl_http', label: 'PHP - pecl_http' },
  { lang: 'powershell', value: 'PowerShell - RestMethod', label: 'PowerShell - RestMethod' },
  { lang: 'python', value: 'Python - http.client', label: 'Python - http.client' },
  { lang: 'python', value: 'Python - Requests', label: 'Python - Requests' },
  { lang: 'r', value: 'R - RCurl', label: 'R - RCurl' },
  { lang: 'r', value: 'R - httr', label: 'R - httr' },
  { lang: 'ruby', value: 'Ruby - Net::HTTP', label: 'Ruby - Net::HTTP' },
  { lang: 'shell', value: 'Shell - Httpie', label: 'Shell - Httpie' },
  { lang: 'shell', value: 'Shell - wget', label: 'Shell - wget' },
  { lang: 'swift', value: 'Swift - URLSession', label: 'Swift - URLSession' },
];

const sections = [
  { id: 1, url: 'api-reference', name: 'API Reference', main: true },
  { id: 2, url: 'api-collection', name: 'Grufity API collection', main: true },
  { id: 3, url: 'stock-api', name: "Stock API's", main: true },
  { id: 4, url: 'latest-stock-metrics', name: 'Get Latest Stock Metrics', main: false },
  { id: 5, url: 'historical-metrics-data', name: 'Get Historical Metrics Data', main: false },
  { id: 6, url: 'health-check', name: 'Health Check', main: true },
  { id: 7, url: 'health-check-api', name: 'Grufity API Health Check', main: false },
];

const libraries = [
  { id: 1, label: 'r', lang: 'R - RCurl' },
  { id: 2, label: 'ruby', lang: 'Ruby - Net::HTTP' },
  { id: 3, label: 'python', lang: 'Python - http.client' },
  { id: 4, label: 'php', lang: 'PHP - cURL' },
  { id: 5, label: 'java', lang: 'Java - OkHttp' },
  { id: 6, label: 'nodejs', lang: 'NodeJs - Axios' },
  { id: 7, label: 'go', lang: 'Go - Native' },
];

const SeriesAbbreviations = [
  { id: 1, name: 'Revenue', abb: 'REV' },
  { id: 2, name: 'Net Income', abb: 'NETINC' },
  { id: 3, name: 'Cost of Revenue', abb: 'COSTREV' },
  { id: 4, name: 'Earnings Before Taxes', abb: 'EBT' },
  { id: 5, name: 'Assets', abb: 'ASSETS' },
  { id: 6, name: 'Liabilities', abb: 'LIABS' },
  { id: 7, name: "Shareholder's Equity", abb: 'SEQ' },
  { id: 8, name: 'Cash and Cash Equivalents', abb: 'CASHEQ' },
  { id: 9, name: 'Depreciation Amortization', abb: 'DEPAMOR' },
  { id: 10, name: 'Long Term Debt', abb: 'DEBT.LT' },
  { id: 11, name: 'Cashflow From Operations', abb: 'CFOPER' },
  { id: 12, name: 'Cashflow From Financing Activities', abb: 'CFFIN' },
  { id: 13, name: 'Cashflow From Investing Activities', abb: 'CFINV' },
  { id: 14, name: 'Price to Sales', abb: 'PS' },
  { id: 15, name: 'Price to Earnings', abb: 'PE' },
  { id: 16, name: 'Return on Equity', abb: 'ROE' },
  { id: 17, name: 'Return on Assets', abb: 'ROA' },
];

// Scoped gradient IDs for SVGs to prevent bleed-through
const TAB_ITEMS = [
  {
    label: "R",
    icon: (
      <svg className="langIcon-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 724 561">
        <defs>
          <linearGradient id="lang-r-grad-1" x1="0" x2="1" y1="0" y2="1" gradientUnits="objectBoundingBox" spreadMethod="pad">
            <stop offset="0" stopColor="rgb(203,206,208)" stopOpacity="1" />
            <stop offset="1" stopColor="rgb(132,131,139)" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="lang-r-grad-2" x1="0" x2="1" y1="0" y2="1" gradientUnits="objectBoundingBox" spreadMethod="pad">
            <stop offset="0" stopColor="rgb(39,109,195)" stopOpacity="1" />
            <stop offset="1" stopColor="rgb(22,92,170)" stopOpacity="1" />
          </linearGradient>
        </defs>
        <path d="M361.453,485.937 C162.329,485.937 0.906,377.828 0.906,244.469 C0.906,111.109 162.329,3.000 361.453,3.000 C560.578,3.000 722.000,111.109 722.000,244.469 C722.000,377.828 560.578,485.937 361.453,485.937 ZM416.641,97.406 C265.289,97.406 142.594,171.314 142.594,262.484 C142.594,353.654 265.289,427.562 416.641,427.562 C567.992,427.562 679.687,377.033 679.687,262.484 C679.687,147.971 567.992,97.406 416.641,97.406 Z" fill="url(#lang-r-grad-1)" fillRule="evenodd" />
        <path d="M550.000,377.000 C550.000,377.000 571.822,383.585 584.500,390.000 C588.899,392.226 596.510,396.668 602.000,402.500 C607.378,408.212 610.000,414.000 610.000,414.000 L696.000,559.000 L557.000,559.062 L492.000,437.000 C492.000,437.000 478.690,414.131 470.500,407.500 C463.668,401.969 460.755,400.000 454.000,400.000 C449.298,400.000 420.974,400.000 420.974,400.000 L421.000,558.974 L298.000,559.026 L298.000,152.938 L545.000,152.938 C545.000,152.938 657.500,154.967 657.500,262.000 C657.500,369.033 550.000,377.000 550.000,377.000 ZM496.500,241.024 L422.037,240.976 L422.000,310.026 L496.500,310.002 C496.500,310.002 531.000,309.895 531.000,274.877 C531.000,239.155 496.500,241.024 496.500,241.024 Z" fill="url(#lang-r-grad-2)" fillRule="evenodd" />
      </svg>
    )
  },
  { label: "Ruby", icon: <svg className="langIcon-svg" viewBox="4 4 28 28" xmlns="http://www.w3.org/2000/svg"><path d="M23.476 5.076c2.78.481 3.569 2.382 3.51 4.373L27 9.42l-1.251 16.398-16.266 1.114h.014c-1.35-.057-4.36-.18-4.497-4.388l1.508-2.75 2.584 6.037.461 1.075 2.571-8.382-.027.006.014-.028 8.484 2.71-1.28-4.978-.907-3.575 8.085-.522-.564-.467-5.804-4.732 3.354-1.871-.003.01zM9.703 9.65c3.264-3.238 7.477-5.152 9.095-3.52 1.615 1.63-.097 5.597-3.367 8.833-3.266 3.238-7.428 5.256-9.042 3.628-1.62-1.63.041-5.7 3.31-8.938l.004-.003z" fill="#D91505"></path></svg> },
  { label: "Python", icon: <svg className="langIcon-svg" viewBox="0 0 256 255" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid"><path d="M126.916.072c-64.832 0-60.784 28.115-60.784 28.115l.072 29.128h61.868v8.745H41.631S.145 61.355.145 126.77c0 65.417 36.21 63.097 36.21 63.097h21.61v-30.356s-1.165-36.21 35.632-36.21h61.362s34.475.557 34.475-33.319V33.97S194.67.072 126.916.072zM92.802 19.66a11.12 11.12 0 0111.13 11.13 11.12 11.12 0 01-11.13 11.13 11.12 11.12 0 01-11.13-11.13 11.12 11.12 0 0111.13-11.13z" fill="#3372a7"></path><path d="M128.757 254.126c64.832 0 60.784-28.115 60.784-28.115l-.072-29.127H127.6v-8.745h86.441s41.486 4.705 41.486-60.712c0-65.416-36.21-63.096-36.21-63.096h-21.61v30.355s1.165 36.21-35.632 36.21h-61.362s-34.475-.557-34.475 33.32v56.013s-5.235 33.897 62.518 33.897zm34.114-19.586a11.12 11.12 0 01-11.13-11.13 11.12 11.12 0 0111.13-11.131 11.12 11.12 0 0111.13 11.13 11.12 11.12 0 01-11.13 11.13z" fill="#ffd235"></path></svg> },
  { label: "PHP", icon: <svg className="langIcon-svg" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><g fill="#6181B6" fillRule="evenodd"><path d="M9.762 14.509h-1.18l-.643 3.31h1.047c.695 0 1.213-.132 1.553-.393.34-.262.569-.698.687-1.311.115-.588.063-1.002-.155-1.244-.218-.241-.653-.362-1.308-.362z"></path><path d="M16 8.86c-8.284 0-15 3.533-15 7.89s6.716 7.891 15 7.891c8.284 0 15-3.533 15-7.891s-6.716-7.89-15-7.89zm-4.075 9.314a3.034 3.034 0 01-1.146.688c-.42.135-.956.205-1.606.205H7.696l-.408 2.102H5.564l1.538-7.908h3.312c.996 0 1.723.261 2.18.785.458.523.595 1.253.412 2.19-.07.371-.198.729-.38 1.059a3.57 3.57 0 01-.702.879zm5.03.893l.679-3.498c.079-.398.049-.67-.085-.814-.134-.145-.42-.218-.859-.218h-1.365l-.88 4.532h-1.71l1.538-7.909h1.708l-.408 2.103h1.522c.959 0 1.619.167 1.983.5s.472.876.328 1.625l-.715 3.68h-1.736zm9.496-2.83a3.477 3.477 0 01-.381 1.058 3.57 3.57 0 01-.701.879 3.05 3.05 0 01-1.147.688c-.42.135-.956.205-1.607.205H21.14l-.409 2.103H19.01l1.537-7.908h3.312c.996 0 1.722.262 2.18.785.457.522.596 1.252.413 2.19z" fillRule="nonzero"></path><path d="M23.207 14.509H22.03l-.645 3.31h1.047c.696 0 1.214-.132 1.553-.393.34-.262.568-.698.688-1.311.115-.588.062-1.002-.156-1.244-.217-.241-.655-.362-1.309-.362z"></path></g></svg> },
  { label: "Java", icon: <svg className="langIcon-svg" viewBox="0 0 256 346" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid"><path d="M82.55 267.47s-13.2 7.68 9.4 10.28c27.37 3.12 41.35 2.67 71.51-3.04 0 0 7.93 4.97 19 9.28-67.6 28.98-153.01-1.68-99.9-16.52m-8.26-37.81s-14.81 10.96 7.8 13.3c29.23 3.01 52.32 3.26 92.27-4.44 0 0 5.53 5.6 14.22 8.67-81.75 23.9-172.8 1.89-114.3-17.53" fill="#5382A1"></path><path d="M143.94 165.51c16.66 19.19-4.38 36.45-4.38 36.45s42.3-21.84 22.88-49.19c-18.15-25.5-32.06-38.17 43.27-81.86 0 0-118.24 29.53-61.77 94.6" fill="#E76F00"></path><path d="M233.36 295.44s9.77 8.05-10.75 14.28c-39.03 11.82-162.44 15.39-196.72.47-12.32-5.36 10.79-12.8 18.06-14.37 7.58-1.64 11.91-1.33 11.91-1.33-13.7-9.66-88.58 18.95-38.03 27.15 137.85 22.35 251.3-10.07 215.53-26.2M88.9 190.48s-62.77 14.9-22.23 20.32c17.12 2.3 51.25 1.78 83.03-.89 25.98-2.19 52.07-6.85 52.07-6.85s-9.16 3.93-15.8 8.45c-63.74 16.77-186.88 8.97-151.43-8.18 29.98-14.5 54.36-12.85 54.36-12.85m112.6 62.94c64.8-33.67 34.84-66.03 13.93-61.67-5.12 1.07-7.4 2-7.4 2s1.9-2.99 5.53-4.28c41.37-14.54 73.19 42.9-13.36 65.65 0 0 1-.9 1.3-1.7" fill="#5382A1"></path><path d="M162.44.37s35.89 35.9-34.04 91.1c-56.07 44.28-12.78 69.53-.02 98.38-32.73-29.53-56.75-55.53-40.64-79.72C111.4 74.6 176.92 57.39 162.44.37" fill="#E76F00"></path><path d="M95.27 344.67c62.2 3.98 157.71-2.21 159.97-31.64 0 0-4.35 11.15-51.4 20.01-53.09 10-118.57 8.83-157.4 2.42 0 0 7.95 6.58 48.83 9.2" fill="#5382A1"></path></svg> },
  { label: "Node.js", icon: <svg className="langIcon-svg" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><path d="M15.516 28.952a2.09 2.09 0 01-1.002-.263l-3.17-1.883c-.478-.262-.24-.358-.096-.405.644-.215.763-.263 1.43-.644.072-.048.167-.024.239.024l2.432 1.454c.095.048.214.048.286 0l9.513-5.507c.095-.048.143-.143.143-.263v-10.99c0-.12-.048-.215-.143-.263L15.635 4.73c-.096-.048-.215-.048-.286 0l-9.513 5.483c-.096.048-.143.167-.143.262v10.991c0 .096.047.215.143.263l2.599 1.502c1.406.715 2.288-.12 2.288-.954V11.428a.29.29 0 01.286-.286h1.216a.29.29 0 01.287.286v10.848c0 1.884-1.026 2.98-2.814 2.98-.548 0-.977 0-2.193-.596L5 23.23A2.012 2.012 0 014 21.49V10.497c0-.715.381-1.383 1.001-1.74l9.513-5.508a2.128 2.128 0 012.003 0l9.513 5.508a2.012 2.012 0 011.001 1.74V21.49c0 .716-.381 1.383-1.001 1.74l-9.513 5.508a2.44 2.44 0 01-1.001.215zm2.932-7.558c-4.172 0-5.03-1.907-5.03-3.529a.29.29 0 01.286-.286h1.24c.142 0 .262.096.262.239.19 1.263.739 1.883 3.266 1.883 2.003 0 2.861-.453 2.861-1.526 0-.62-.238-1.073-3.362-1.383-2.598-.262-4.22-.834-4.22-2.908 0-1.931 1.622-3.076 4.34-3.076 3.051 0 4.553 1.05 4.744 3.338a.385.385 0 01-.072.215c-.047.047-.119.095-.19.095h-1.24a.28.28 0 01-.262-.215c-.286-1.31-1.025-1.74-2.98-1.74-2.194 0-2.456.763-2.456 1.335 0 .691.31.906 3.266 1.288 2.933.381 4.315.93 4.315 2.98-.023 2.098-1.74 3.29-4.768 3.29z" fill="#89D42C"></path></svg> },
  { label: "Go", icon: <svg className="langIcon-svg" viewBox="2 2 30 30" xmlns="http://www.w3.org/2000/svg"><g fill="#00ACD7" fillRule="evenodd"><path d="M3.35 13.5c-.06 0-.07-.04-.05-.08l.3-.4c.04-.03.11-.06.17-.06h5.17c.06 0 .07.04.05.08l-.25.38c-.03.04-.1.09-.15.09l-5.24-.02zm-2.19 1.33c-.06 0-.07-.03-.04-.08l.3-.39c.03-.04.1-.07.16-.07h6.6c.07 0 .1.04.08.09l-.12.34c-.01.06-.07.1-.13.1H1.16zm3.51 1.33c-.06 0-.08-.04-.05-.09l.2-.36c.04-.04.1-.09.15-.09h2.9c.06 0 .09.05.09.1l-.03.35c0 .06-.06.1-.1.1l-3.16-.01zm15.04-2.93l-2.43.64c-.22.06-.24.07-.42-.15-.22-.24-.38-.4-.69-.55a2.52 2.52 0 00-2.62.22 3.08 3.08 0 00-1.48 2.75 2.27 2.27 0 001.96 2.28c.98.13 1.81-.22 2.46-.96l.4-.53h-2.8c-.3 0-.38-.2-.28-.44.19-.45.54-1.2.74-1.58a.4.4 0 01.36-.23h5.28c-.03.4-.03.78-.09 1.18-.16 1.04-.55 2-1.19 2.84a6.04 6.04 0 01-4.13 2.46 5.13 5.13 0 01-3.9-.96A4.55 4.55 0 019.04 17c-.18-1.58.28-3 1.24-4.25a6.43 6.43 0 014.05-2.5 5 5 0 013.84.7c.77.5 1.32 1.2 1.69 2.05.08.13.02.2-.15.24z"></path><path d="M24.5 21.25a5.44 5.44 0 01-3.53-1.28 4.55 4.55 0 01-1.56-2.8 5.53 5.53 0 011.17-4.37 6.08 6.08 0 014.06-2.42 5.38 5.38 0 014.13.74 4.51 4.51 0 012.04 3.23 5.6 5.6 0 01-1.67 4.91 6.64 6.64 0 01-3.47 1.86c-.4.07-.79.08-1.16.13zm3.46-5.86c-.02-.19-.02-.33-.05-.48A2.39 2.39 0 0024.96 13a3.17 3.17 0 00-2.54 2.52 2.39 2.39 0 001.33 2.74c.8.34 1.6.3 2.37-.1a3.17 3.17 0 001.84-2.76z" fillRule="nonzero"></path></g></svg> }
];


/* ==============================================================================
   4. MODULAR REUSABLE COMPONENTS
   ============================================================================== */

// A) Isolated Copy URL Button (Fixes global clipboard shared state bug)
const CopyUrlButton = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) { await navigator.clipboard.writeText(url); }
      else {
        const el = document.createElement('textarea');
        el.value = url; document.body.appendChild(el); el.select();
        document.execCommand('copy'); document.body.removeChild(el);
      }
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch (e) { console.error("Copy failed", e); }
  };

  return (
    <LightTooltip title={copied ? "Copied!" : "Click to copy"} placement="top" disableInteractive>
      <ContentPasteTwoToneIcon aria-label="Copy URL" sx={{ fontSize: '1.1rem', ml: 'auto', cursor: 'pointer' }} onClick={handleCopy} />
    </LightTooltip>
  );
};

// B) Lazy Highlighting Component with Skeleton Fallback
const LazySyntaxHighlighter = React.memo(({ language, code, style, showLineNumbers }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ minHeight: isVisible ? 'auto' : '150px' }}>
      {isVisible ? (
        <SyntaxHighlighter language={language} style={style} showLineNumbers={showLineNumbers}>
          {code}
        </SyntaxHighlighter>
      ) : (
        <Box sx={{ height: 150, bgcolor: THEME_COLORS.bgCode, borderRadius: '7px', opacity: 0.5, animation: 'pulse 1.5s infinite' }} />
      )}
    </Box>
  );
});

// C) Modular Request Code Block
const CodeRequestBlock = React.memo(({ vscDarkPlus, langCode, langValue, onLangChange, codeSnippetData }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  const snippet = codeSnippetData[langValue] || '// Code snippet not available for this language.';

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) { await navigator.clipboard.writeText(snippet); }
      else {
        const el = document.createElement('textarea');
        el.value = snippet; document.body.appendChild(el); el.select();
        document.execCommand('copy'); document.body.removeChild(el);
      }
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch (e) { console.error(e); }
  };

  return (
    <Box component="div" sx={{ background: THEME_COLORS.bgPanel, borderRadius: '7px', mb: 3, maxWidth: { md: '34rem', sm: '100%', xs: "100%", lg: '50rem' }, border: `1px solid ${THEME_COLORS.border}` }}>
      <Box component="div" sx={{ px: 2, py: 1, borderBottom: `1px solid ${THEME_COLORS.border}`, color: THEME_COLORS.textMuted, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: "space-between" }}>
        <Box component="div" sx={{ fontSize: ".8rem", fontWeight: 600 }}><span style={{ color: THEME_COLORS.methodGet }}>GET</span>&nbsp;&nbsp;REQUEST</Box>
        <Stack direction="row" justifyContent="center" alignItems="center" divider={<Divider orientation="vertical" flexItem sx={{ borderColor: '#E7EBF01f' }} />} spacing={2}>
          <TextField
            sx={{
              '& .MuiInput-root:before, .MuiInput-root:hover:not(.Mui-disabled):before, .MuiInput-root:after': { borderBottom: 0 },
              '& .MuiInput-root': { color: THEME_COLORS.textMuted, fontSize: '.87rem' },
              '& .MuiNativeSelect-icon ': { color: THEME_COLORS.textMuted, fontSize: '1.1rem' },
              '& .MuiInput-input:focus': { backgroundColor: "transparent" },
              '& .MuiInput-input': { pb: .3, pl: .7 }
            }}
            size="small"
            select
            value={langValue}
            SelectProps={{ native: true, IconComponent: UnfoldMoreRoundedIcon }}
            variant="standard"
            onChange={onLangChange}
          >
            {languages.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </TextField>
          <LightTooltip title={copied ? "Copied!" : "Click to copy"} arrow placement="top" disableInteractive>
            <ContentPasteTwoToneIcon aria-label="Copy request snippet" sx={{ fontSize: '1.1rem', ml: 'auto', cursor: 'pointer', '&:hover': { color: '#fff' } }} onClick={handleCopy} />
          </LightTooltip>
        </Stack>
      </Box>
      <Box component="div" sx={{ background: THEME_COLORS.bgCode }}>
        <Chip size="small" sx={{ borderRadius: "7px", color: THEME_COLORS.textMuted, border: '1px solid #e3e8ee0a', mx: 1.5, mt: 1.5, background: '#2a405a' }} label={langCode} />
      </Box>
      <LazySyntaxHighlighter language={langCode} style={vscDarkPlus} showLineNumbers code={snippet} />
    </Box>
  );
});

// D) Modular Response Code Block
const CodeResponseBlock = React.memo(({ vscDarkPlus, responseSnippet }) => {
  return (
    <Box component="div" sx={{ background: THEME_COLORS.bgPanel, borderRadius: '7px', mb: 3, maxWidth: { md: '34rem', sm: '100%', xs: "100%", lg: '50rem' }, border: `1px solid ${THEME_COLORS.border}` }}>
      <Box component="div" sx={{ px: 2, py: 1, borderBottom: `1px solid ${THEME_COLORS.border}`, color: THEME_COLORS.textMuted, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: "space-between" }}>
        <Box component="div" sx={{ fontSize: ".8rem", fontWeight: 600 }}>RESPONSE</Box>
        <Chip size="small" sx={{ borderRadius: "7px", color: THEME_COLORS.textMuted, border: '1px solid #e3e8ee0a', background: '#1c334c' }} label="200 OK" />
      </Box>
      <Box component="div" className="res" sx={{ background: THEME_COLORS.bgCode, borderRadius: '0 7px', maxHeight: '30rem', overflow: 'auto' }}>
        <Box component="div" sx={{ position: 'sticky', top: 0, zIndex: 10, background: THEME_COLORS.bgCode, pt: 1.5, pl: 1.5 }}>
          <Chip size="small" sx={{ borderRadius: "7px", color: THEME_COLORS.textMuted, border: '1px solid #e3e8ee0a', '& .MuiChip-label': { fontSize: '.7rem' }, background: '#2a405a' }} label="JSON" />
        </Box>
        <LazySyntaxHighlighter language="json" style={vscDarkPlus} showLineNumbers={false} code={responseSnippet} />
      </Box>
    </Box>
  );
});


/* ==============================================================================
   5. MAIN API PAGE COMPONENT
   ============================================================================== */

const API = () => {
  const router = useRouter();

  const [selectedSection, setSelectedSection] = useState('api-reference');
  const [selectedSectionName, setSelectedSectionName] = useState('API Reference');
  const [anchorEl, setAnchorEl] = useState(null);

  // Theme state
  const [vscDarkPlus, setVscDarkPlus] = useState({});
  const [themeLoaded, setThemeLoaded] = useState(false);

  // URL as Single Source of Truth for Language
  const activeUrlLang = router.query.lang;
  const activeLanguageObj = languages.find(l => l.lang === activeUrlLang) || languages.find(l => l.lang === 'r');
  const langValue = activeLanguageObj.value;
  const langCode = activeLanguageObj.lang;

  // Load Theme Safely
  useEffect(() => {
    let isMounted = true;
    import('react-syntax-highlighter/dist/cjs/styles/prism')
      .then(mod => {
        if (isMounted) { setVscDarkPlus(mod.vscDarkPlus); setThemeLoaded(true); }
      })
      .catch(() => { if (isMounted) setThemeLoaded(true); });

    return () => { isMounted = false; };
  }, []);

  // Scroll Spy Logic (Fixed Race Condition & Intersection Mismatches)
  useEffect(() => {
    const visibleSections = new Map();
    const observerOptions = { root: null, rootMargin: '-15% 0px -50% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          visibleSections.set(entry.target.id, entry.intersectionRatio);
        } else {
          visibleSections.delete(entry.target.id);
        }
      });

      if (visibleSections.size > 0) {
        let maxRatio = 0;
        let activeId = null;
        for (const [id, ratio] of visibleSections.entries()) {
          if (ratio > maxRatio) { maxRatio = ratio; activeId = id; }
        }

        if (activeId && activeId !== selectedSection) {
          setSelectedSection(activeId);
          const sectionData = sections.find(s => s.url === activeId);
          if (sectionData) setSelectedSectionName(sectionData.name);

          if (window.history.replaceState) {
            const newUrl = `${window.location.pathname}${window.location.search}#${activeId}`;
            window.history.replaceState(null, '', newUrl);
          }
        }
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    sections.forEach((section) => {
      const element = document.getElementById(section.url);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [selectedSection]);

  // Sync initial state on load if URL has hash
  useEffect(() => {
    if (window.location.hash) {
      const hash = window.location.hash.replace('#', '');
      setSelectedSection(hash);
      const sectionData = sections.find(s => s.url === hash);
      if (sectionData) setSelectedSectionName(sectionData.name);
    }
  }, []);

  // Handle Tree View / Menu Clicks
  const handleSectionClick = (e, sectionUrl, sectionName) => {
    e.preventDefault();
    setAnchorEl(null);
    setSelectedSection(sectionUrl);
    setSelectedSectionName(sectionName);

    const element = document.getElementById(sectionUrl);
    if (element) {
      const headerOffset = 90; // Increased for mobile AppBar clearance
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });

      if (window.history.pushState) {
        const newUrl = `${window.location.pathname}${window.location.search}#${sectionUrl}`;
        window.history.pushState(null, '', newUrl);
      }
    }
  };

  const handleLangChange = useCallback((event) => {
    const newValue = languages.find(d => d.value === event.target.value);
    if (newValue) {
      router.push(
        { pathname: router.pathname, query: { lang: newValue.lang } },
        `${router.pathname}?lang=${newValue.lang}#${selectedSection}`,
        { shallow: true }
      );
    }
  }, [router, selectedSection]);

  const handleLangTabChange = (event, newValue) => {
    if (newValue === false) return; // Ignore if mapping fails
    const library = libraries[newValue];
    if (library?.lang) {
      const targetLang = languages.find(l => l.value === library.lang);
      if (targetLang) {
        router.push(
          { pathname: router.pathname, query: { lang: targetLang.lang } },
          `${router.pathname}?lang=${targetLang.lang}#${selectedSection}`,
          { shallow: true }
        );
      }
    }
  };

  const activeTabIndex = useMemo(() => libraries.findIndex(l => l.lang === langValue), [langValue]);

  return (
    <Container maxWidth="xl" sx={{ position: 'relative' }}>
      <Box sx={{
        display: 'flex', background: 'transparent', '& a': { color: THEME_COLORS.primary },
        /* Mac-Style Hover-Only Scrollbars */
        '& pre::-webkit-scrollbar, .res::-webkit-scrollbar': { width: '8px', height: '8px' },
        '& pre, .res': { scrollbarWidth: 'thin', scrollbarColor: '#344e6f transparent', transition: 'scrollbar-color .3s ease' },
        '& pre::-webkit-scrollbar-track, .res::-webkit-scrollbar-track': { background: 'transparent' },
        '& pre::-webkit-scrollbar-thumb, .res::-webkit-scrollbar-thumb': { backgroundColor: 'transparent', borderRadius: '6px', border: `2px solid ${THEME_COLORS.bgCode}` },
        '& pre:hover::-webkit-scrollbar-thumb, .res:hover::-webkit-scrollbar-thumb': { backgroundColor: '#344e6f' },
        '& pre::-webkit-scrollbar-thumb:hover, .res::-webkit-scrollbar-thumb:hover': { backgroundColor: '#4a6587' }
      }}>
        <CssBaseline />

        {/* Sidebar */}
        <Drawer variant="permanent" sx={{
          width: 240, flexShrink: 0, display: { xs: 'none', sm: 'none', md: 'block' }, zIndex: (theme) => theme.zIndex.appBar - 2,
          [`& .MuiDrawer-paper`]: { width: 240, boxSizing: 'border-box', background: 'tranasparent', borderRight: '1px solid #1234561f' },
        }}>
          <Toolbar />
          <Box sx={{ overflow: 'auto', mt: 5 }}>
            <SimpleTreeView
              aria-label="grufity api tree"
              defaultExpandedItems={['group-1', 'group-2', 'group-3', 'group-4']}
              selectedItems={selectedSection}
              multiSelect={false}
              onSelectedItemsChange={(event, itemId) => {
                if (itemId && !itemId.startsWith('group-')) {
                  const sec = sections.find(s => s.url === itemId);
                  if (sec) handleSectionClick(event, sec.url, sec.name);
                }
              }}
              sx={{ flexGrow: 1, maxWidth: 400 }}
            >
              <StyledTreeItem itemId="group-1" labelText="API Reference" sx={{ fontWeight: 600 }}>
                <StyledTreeItem itemId="api-reference" labelText="Overview" />
              </StyledTreeItem>
              <StyledTreeItem itemId="group-2" labelText="Grufity API collection" sx={{ fontWeight: 600 }}>
                <StyledTreeItem itemId="api-collection" labelText="Overview" />
              </StyledTreeItem>
              <StyledTreeItem itemId="group-3" labelText="Stock API's" sx={{ fontWeight: 600 }}>
                <StyledTreeItem itemId="stock-api" labelText="Overview" />
                <StyledTreeItem itemId="latest-stock-metrics" labelText="Get Latest Stock Metrics" />
                <StyledTreeItem itemId="historical-metrics-data" labelText="Get Historical Metrics Data" />
              </StyledTreeItem>
              <StyledTreeItem itemId="group-4" labelText="Health Check" sx={{ fontWeight: 600 }}>
                <StyledTreeItem itemId="health-check" labelText="Overview" />
                <StyledTreeItem itemId="health-check-api" labelText="Grufity API Health Check" />
              </StyledTreeItem>
            </SimpleTreeView>
          </Box>
        </Drawer>

        {/* Main Content Area */}
        <Box component="main" sx={{ flexGrow: 1, pt: 0, pb: 3, width: '100%' }}>

          {/* Mobile Header */}
          <AppBar component="nav" position="static" elevation={0} sx={{ background: 'transparent' }}>
            <Toolbar sx={{ display: { sm: 'flex', xs: 'flex', md: 'none' }, justifyContent: 'flex-start', pt: 2, pb: 1 }}>
              <Button size="small" id="side-drawer-menu" variant="contained" disableElevation
                onClick={(e) => setAnchorEl(e.currentTarget)} endIcon={<KeyboardArrowDownIcon />}
                sx={{ textTransform: "capitalize", background: '#001E42' }}
              >
                {selectedSectionName}
              </Button>
              <StyledMenu id="api-section-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {sections.map(d => (
                  <MenuItem key={`menu-${d.id}`} onClick={(e) => handleSectionClick(e, d.url, d.name)} selected={d.url === selectedSection}
                    sx={{ textTransform: d.main ? "uppercase" : 'capitalize', fontWeight: d.main ? 600 : 500, mt: d.main ? .7 : 0, fontSize: d.main ? ".8em" : ".87rem" }}
                  >
                    {d.name}
                  </MenuItem>
                ))}
              </StyledMenu>
            </Toolbar>
          </AppBar>

          {/* Section 1: API Reference */}
          <Box component="section" id="api-reference" sx={{ px: { md: 10, sm: 5, xs: 1.2 }, pb: 10, pt: { md: 0, sm: 3, xs: 3 } }}>
            <Typography variant="h2" sx={SECTION_HEADING_SX}>
              API Reference <Link href="#api-reference" onClick={(e) => handleSectionClick(e, 'api-reference', 'API Reference')} sx={{ ml: 3, verticalAlign: 'middle' }}><LinkRoundedIcon /></Link>
            </Typography>
            <Grid container>
              <Grid size={{ md: 5.5, sm: 12, xs: 12 }} sx={{ pr: { md: 4, sm: 0, xs: 0 } }}>
                <Typography component="div" sx={{ pb: 1, color: THEME_COLORS.textDark }}>The Grufity API is organized around REST. Our API has predictable resource-oriented URLs, accepts form-encoded request bodies, returns JSON-encoded responses, and uses standard HTTP response codes, authentication, and verbs.</Typography>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>To authenticate the request, utilize the Grufity API along with the API key available on your <Link underline="hover" href="/profile">profile page</Link>.</Typography>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>The capability to perform bulk requests is not supported by the Grufity API. Each request allows you to work on a single object at a time.</Typography>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>The Grufity API differs for every account as we release new versions and tailor functionality. These docs are customized to your version of the API and display data, which only you can see.</Typography>
              </Grid>
              <Grid size={{ md: 6.5, sm: 12, xs: 12 }} sx={{ pl: { md: 4, sm: 0, xs: 0 }, '& pre': { background: `${THEME_COLORS.bgCode} !important`, my: '0 !important', borderRadius: '0px 0px 7px 7px' } }}>
                <Box component="div" sx={{ position: 'sticky', top: '4rem' }}>
                  <Box component="div" sx={{ background: THEME_COLORS.bgPanel, borderRadius: '7px', mb: 3, maxWidth: { md: '34rem', sm: '100%', xs: "100%", lg: '50rem' }, border: `1px solid ${THEME_COLORS.border}` }}>
                    <Box component="div" sx={{ px: 2, py: 1, borderBottom: `1px solid ${THEME_COLORS.border}`, color: "#E7EBF0", display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: "space-between" }}>
                      <Box component="div" sx={{ fontSize: ".8rem", fontWeight: 600 }}>BASE URL</Box>
                    </Box>
                    {themeLoaded && <SyntaxHighlighter style={vscDarkPlus}>https://api.grufity.com/</SyntaxHighlighter>}
                  </Box>
                  <Box component="div" sx={{ background: THEME_COLORS.bgPanel, borderRadius: '7px', maxWidth: { md: '34rem', sm: '100%', xs: "100%", lg: '50rem' }, border: `1px solid ${THEME_COLORS.border}` }}>
                    <Box component="div" sx={{ px: 2, py: 1, background: THEME_COLORS.bgCode, borderRadius: '5px 5px 0 0', color: "#E7EBF0", display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: "space-between" }}>
                      <Box component="div" sx={{ fontSize: ".8rem", fontWeight: 600 }}>CLIENT LIBRARIES</Box>
                    </Box>
                    <Tabs
                      value={activeTabIndex === -1 ? false : activeTabIndex}
                      onChange={handleLangTabChange}
                      variant="scrollable" scrollButtons={false}
                      sx={{ background: THEME_COLORS.bgCode, '& svg': { width: '24px !important', height: '24px !important' }, '& .MuiTab-root': { color: THEME_COLORS.textMuted, minWidth: '4.85rem', minHeight: '70px', justifyContent: 'space-between', textTransform: 'none' }, '& .MuiTab-root.Mui-selected': { color: '#fff' }, '& .MuiTabs-indicator': { backgroundColor: THEME_COLORS.primaryHover } }}
                    >
                      {TAB_ITEMS.map((tab, index) => (
                        <Tab key={`tab-${index}`} icon={tab.icon} label={tab.label} />
                      ))}
                    </Tabs>
                    <Box component="div" sx={{ px: 2, py: 1, borderTop: `1px solid ${THEME_COLORS.border}`, color: THEME_COLORS.textMuted, fontSize: ".8rem" }}>Select your preferred language.</Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Section 2: API Collection */}
          <Box component="section" id="api-collection" sx={{ px: { md: 10, sm: 5, xs: 1.2 }, borderTop: '1px solid #1234561f', pb: 10 }}>
            <Typography variant="h2" sx={SECTION_HEADING_SX}>
              Grufity API collection <Link href="#api-collection" onClick={(e) => handleSectionClick(e, 'api-collection', 'Grufity API collection')} sx={{ ml: 3, verticalAlign: 'middle' }}><LinkRoundedIcon /></Link>
            </Typography>
            <Grid container>
              <Grid size={{ md: 5.5, sm: 12, xs: 12 }} sx={{ pr: { md: 4, sm: 0, xs: 0 } }}>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>Grufity Developer API's collection contain various API's to fetch various metrics for a stock ticker or macro-economic data. This can be used to get both historical and latest metrics.</Typography>
                <Typography component="div" sx={{ pt: 1, color: THEME_COLORS.textHeading, fontWeight: 600 }}>Step 1: Send requests</Typography>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>RESTful APIs allow you to perform GET operations using the GET HTTP method.</Typography>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>This collection contains request for various sample data. Open each request, put your API key in header and click "Send" to fetch the result. <br /><br />You can find you API key in your <Link underline="hover" href="/profile">profile page</Link>.</Typography>
                <Typography component="div" sx={{ pt: 2.5, color: THEME_COLORS.textHeading, fontWeight: 600 }}>Step 2: View responses</Typography>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>Observe the response tab for status code (<Chip size="small" sx={{ background: THEME_COLORS.chipBg, borderRadius: '7px', color: THEME_COLORS.textDark, '& .MuiChip-label': { px: .6 } }} label='200 OK' />), response time, and size.</Typography>
              </Grid>
              <Grid size={{ md: 6.5, sm: 12, xs: 12 }}>
                <TableContainer sx={{ maxWidth: { md: '34rem', sm: '100%', xs: "100%", lg: '50rem' }, '& td, & th': { background: 'transparent', borderBottom: '1px solid #1234561f', color: THEME_COLORS.textHeading }, borderRadius: '7px', position: 'sticky', top: '4rem' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow><TableCell colSpan={2} sx={{ fontWeight: 600, px: 0 }}>AUTHORIZATION <span style={{ fontWeight: 500, color: THEME_COLORS.textDark }}>&nbsp;API Key</span></TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ px: 0 }} component="th" scope="row">Key</TableCell>
                        <TableCell sx={{ px: 0 }} align="right">X-API-KEY</TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ px: 0 }} component="th" scope="row">Value</TableCell>
                        <TableCell sx={{ px: 0 }} align="right">{`<Please Replace this with`} <Link underline="hover" href="/profile"> your API Key</Link>{`>`}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>

          {/* Section 3: Stock APIs Overview */}
          <Box component="section" id="stock-api" sx={{ px: { md: 10, sm: 5, xs: 1.2 }, borderTop: '1px solid #1234561f', pb: 10 }}>
            <Typography variant="h2" sx={SECTION_HEADING_SX}>
              Stock API's <Link href="#stock-api" onClick={(e) => handleSectionClick(e, 'stock-api', "Stock API's")} sx={{ ml: 3, verticalAlign: 'middle' }}><LinkRoundedIcon /></Link>
            </Typography>
            <Grid container>
              <Grid size={{ md: 5.5, sm: 12, xs: 12 }} sx={{ pr: { md: 4, sm: 0, xs: 0 } }}>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>Collection of APIs to fetch Stock Metrics and Historical Stocks and Macro-Economic data.</Typography>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}><span style={{ color: THEME_COLORS.textHeading }}>Support:</span> <Link underline="hover" href="mailto:contact@grufity.com" target="_blank">contact@grufity.com</Link> <br />(For any specific issues feel free to write to us)</Typography>
              </Grid>
              <Grid size={{ md: 6.5, sm: 12, xs: 12 }}>
                <TableContainer sx={{ maxWidth: { md: '34rem', sm: '100%', xs: "100%", lg: '50rem' }, '& td, & th': { background: 'transparent', borderBottom: '1px solid #1234561f', color: THEME_COLORS.textHeading }, borderRadius: '7px', position: 'sticky', top: '4rem' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow><TableCell sx={{ fontWeight: 600, px: 0 }}>AUTHORIZATION <span style={{ fontWeight: 500, color: THEME_COLORS.textDark }}>&nbsp;API Key</span></TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ px: 0 }} component="th" scope="row">This folder is using API Key from collection <Link underline="hover" href="#api-collection" onClick={(e) => handleSectionClick(e, 'api-collection', 'Grufity API collection')}>Grufity API collection</Link></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>

          {/* Section 4: Latest Stock Metrics */}
          <Box component="section" id="latest-stock-metrics" sx={{ px: { md: 10, sm: 5, xs: 1.2 }, borderTop: '1px solid #1234561f', pb: 10 }}>
            <Typography variant="h2" sx={SECTION_HEADING_SX}>
              Get Latest Stock Metrics <Link href="#latest-stock-metrics" onClick={(e) => handleSectionClick(e, 'latest-stock-metrics', 'Get Latest Stock Metrics')} sx={{ ml: 3, verticalAlign: 'middle' }}><LinkRoundedIcon /></Link>
            </Typography>
            <Grid container>
              <Grid size={{ md: 5.5, sm: 12, xs: 12 }} sx={{ pr: { md: 4, sm: 0, xs: 0 } }}>
                <Box component="pre" sx={{ pt: 1.3, px: 2, pb: 1, color: THEME_COLORS.textDark, border: '1px solid #e3e8ee0a', background: THEME_COLORS.chipBg, borderRadius: '7px', display: 'flex', alignItems: 'center' }}>
                  <Typography component="code" sx={{ fontSize: '.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    https://api.grufity.com/stock/getallmetrics?ticker=AAPL
                  </Typography>
                  <CopyUrlButton url="https://api.grufity.com/stock/getallmetrics?ticker=AAPL" />
                </Box>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>
                  This GET request is used to "get" latest metrics data for a ticker. Use query parameters to specify the ticker you want data on (e.g., in this request, we have&nbsp;<Chip size="small" sx={{ background: THEME_COLORS.chipBg, borderRadius: '7px', color: THEME_COLORS.textDark }} label='ticker=AAPL' />).
                </Typography>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>
                  A successful GET response will have a&nbsp;<Chip size="small" sx={{ background: THEME_COLORS.chipBg, borderRadius: '7px', color: THEME_COLORS.textDark }} label='200 OK' />&nbsp;status, and will include JSON data with latest stock metrics for the specified stock.
                </Typography>
                <TableContainer sx={{ maxWidth: { md: '34rem', sm: '100%', xs: "100%", lg: '50rem' }, '& td, & th': { background: 'transparent', borderBottom: '1px solid #1234561f', color: THEME_COLORS.textHeading }, borderRadius: '7px', my: 3 }}>
                  <Table size="small">
                    <TableHead><TableRow><TableCell colSpan={2} sx={{ fontWeight: 600, px: 0 }}>HEADERS</TableCell></TableRow></TableHead>
                    <TableBody><TableRow><TableCell sx={{ px: 0 }}>X-API-KEY</TableCell><TableCell sx={{ px: 0 }} align="right">{`<Please Replace this with `} <Link href="/profile">your API Key</Link>{`>`}</TableCell></TableRow></TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid size={{ md: 6.5, sm: 12, xs: 12 }} sx={{ pl: { md: 4, sm: 0, xs: 0 }, '& pre': { background: `${THEME_COLORS.bgCode} !important`, my: '0 !important', borderRadius: '0px 0px 7px 7px' } }}>
                <Box sx={{ position: 'sticky', top: '4rem' }}>
                  {themeLoaded && (
                    <>
                      <CodeRequestBlock vscDarkPlus={vscDarkPlus} langCode={langCode} langValue={langValue} onLangChange={handleLangChange} codeSnippetData={stockMetrics} />
                      <CodeResponseBlock vscDarkPlus={vscDarkPlus} responseSnippet={stockMetricsResponse['res']} />
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Section 5: Historical Metrics Data */}
          <Box component="section" id="historical-metrics-data" sx={{ px: { md: 10, sm: 5, xs: 1.2 }, borderTop: '1px solid #1234561f', pb: 10 }}>
            <Typography variant="h2" sx={SECTION_HEADING_SX}>
              Get Historical Metrics Data <Link href="#historical-metrics-data" onClick={(e) => handleSectionClick(e, 'historical-metrics-data', 'Get Historical Metrics Data')} sx={{ ml: 3, verticalAlign: 'middle' }}><LinkRoundedIcon /></Link>
            </Typography>
            <Grid container>
              <Grid size={{ md: 5.5, sm: 12, xs: 12 }} sx={{ pr: { md: 4, sm: 0, xs: 0 } }}>
                <Box component="pre" sx={{ pt: 1.3, px: 2, pb: 1, color: THEME_COLORS.textDark, background: THEME_COLORS.chipBg, borderRadius: '7px', display: 'flex', alignItems: 'center' }}>
                  <Typography component="code" sx={{ fontSize: '.87rem', mr: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    https://api.grufity.com/stock/gethistoricaldata?code=AAPL.REV.TTM
                  </Typography>
                  <CopyUrlButton url="https://api.grufity.com/stock/gethistoricaldata?code=AAPL.REV.TTM" />
                </Box>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark }}>
                  This GET request is used to "get" historical data for a single metrics of a ticker. Use query parameters to specify the metrics code you want data on (e.g., &nbsp;<Chip size="small" sx={{ background: THEME_COLORS.chipBg, borderRadius: '7px', color: THEME_COLORS.textDark }} label='code=AAPL.REV.TTM' />).
                </Typography>
                <TableContainer sx={{ maxWidth: { md: '34rem', sm: '100%', xs: "100%", lg: '50rem' }, border: '1px solid #1234561f', '& td, & th': { background: 'transparent', borderBottom: '1px solid #1234561f', color: THEME_COLORS.textHeading }, borderRadius: '7px', my: 3 }}>
                  <Table size="small">
                    <TableHead><TableRow sx={{ background: THEME_COLORS.chipBg }}><TableCell sx={{ fontWeight: 600 }}>Name</TableCell><TableCell sx={{ fontWeight: 600 }} align="right">Abbreviation</TableCell></TableRow></TableHead>
                    <TableBody>
                      {SeriesAbbreviations.map(d => (
                        <TableRow key={`abbr-${d.id}`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}><TableCell>{d.name}</TableCell><TableCell align="right">{d.abb}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid size={{ md: 6.5, sm: 12, xs: 12 }} sx={{ pl: { md: 4, sm: 0, xs: 0 }, '& pre': { background: `${THEME_COLORS.bgCode} !important`, my: '0 !important', borderRadius: '0px 0px 7px 7px' } }}>
                <Box sx={{ position: 'sticky', top: '4rem' }}>
                  {themeLoaded && (
                    <>
                      <CodeRequestBlock vscDarkPlus={vscDarkPlus} langCode={langCode} langValue={langValue} onLangChange={handleLangChange} codeSnippetData={historicalMetrics} />
                      <CodeResponseBlock vscDarkPlus={vscDarkPlus} responseSnippet={historicalMetricsResponse['res']} />
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Section 6: Health Check */}
          <Box component="section" id="health-check" sx={{ px: { md: 10, sm: 5, xs: 1.2 }, borderTop: '1px solid #1234561f', pb: 10 }}>
            <Typography variant="h2" sx={SECTION_HEADING_SX}>
              Health Check <Link href="#health-check" onClick={(e) => handleSectionClick(e, 'health-check', 'Health Check')} sx={{ ml: 3, verticalAlign: 'middle' }}><LinkRoundedIcon /></Link>
            </Typography>
            <Grid container>
              <Grid size={{ md: 5.5, sm: 12, xs: 12 }} sx={{ pr: { md: 4, sm: 0, xs: 0 } }}>
                <Typography component="div" sx={{ py: 1, color: THEME_COLORS.textDark, mb: 2 }}>
                  The &nbsp;<Chip size="small" sx={{ background: THEME_COLORS.chipBg, borderRadius: '7px', color: THEME_COLORS.textDark, height: '24px' }} label='/health' />&nbsp; endpoints let you check overall health of Grufity API Server.
                </Typography>
              </Grid>
              <Grid size={{ md: 6.5, sm: 12, xs: 12 }} sx={{ pl: { md: 4, sm: 0, xs: 0 }, '& pre': { background: `${THEME_COLORS.bgCode} !important`, my: '0 !important', borderRadius: '0px 0px 7px 7px' } }}>
                <Box id="health-check-api" sx={{ position: 'sticky', top: '4rem' }}>
                  {themeLoaded && (
                    <>
                      <CodeRequestBlock vscDarkPlus={vscDarkPlus} langCode={langCode} langValue={langValue} onLangChange={handleLangChange} codeSnippetData={healthCheck} />
                      <CodeResponseBlock vscDarkPlus={vscDarkPlus} responseSnippet={healthCheckResponse['res']} />
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

        </Box>
      </Box>
    </Container>
  );
};

export async function getStaticProps() {
  const meta_tags = (await getPageMetaTags('/api', false)) || {};
  return {
    props: { meta_tags: meta_tags.getMetaTags?.tags || null },
    revalidate: dataConstants.REVALIDATE_IN.DEFAULT_HOURS,
  };
}

export default API;
